import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import { vueI18nToken } from '~/constants/dependency-injection/tokens'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  NotificationGroup,
  NotificationSettings,
  NotificationSettingsTopic,
  NotificationEvent,
  NotificationUnsubscribeInfo
} from '~/models/notification/types'
import { ActionResult } from '~/models/shared/result'
import { toSnakeCase } from '~/utils/snake-case'
import { Pagination } from '~/models/search/types'
import { AxiosError } from 'axios'
import LoggerService from '~/services/LoggerService'
import SnackbarService from '~/services/snackbar/SnackbarService'
import { VueI18n } from '~/utils/nuxt3-migration'

@containerScoped()
export default class NotificationService {
  constructor(
    @inject(RequestBuilder) private requestBuilder: RequestBuilder,
    @inject(SnackbarService) private snackbar: SnackbarService,
    @inject(LoggerService) public logger: LoggerService,
    @inject(vueI18nToken) private i18n: VueI18n
  ) {}

  public fetchNotificationsSettings(): Promise<{
    groups: NotificationSettings
  }> {
    return this.requestBuilder
      .request('get', '/api/user/notifications/settings/')
      .validate(body => body && body?.data?.groups)
      .send()
  }

  public enableEmailNotification(topic: string): Promise<any> {
    return this.requestBuilder
      .request('put', '/api/user/notifications/settings/email')
      .data({ topic, enabled: true })
      .send()
  }

  public disableEmailNotification(topic: string): Promise<any> {
    return this.requestBuilder
      .request('delete', '/api/user/notifications/settings/email')
      .data({ topic, enabled: false })
      .send()
  }

  public enableAppNotification(topic: string): Promise<any> {
    return this.requestBuilder
      .request('put', '/api/user/notifications/settings/app')
      .data({ topic, enabled: true })
      .send()
  }

  public disableAppNotification(topic: string): Promise<any> {
    return this.requestBuilder
      .request('delete', '/api/user/notifications/settings/app')
      .data({ topic, enabled: false })
      .send()
  }

  public fetchOtherNotifications(): Promise<{
    topics: NotificationSettingsTopic[]
  }> {
    return this.requestBuilder
      .request('get', '/api/user/email-unsubscribe')
      .validate(body => body && body?.data?.topics)
      .send()
  }

  public enableOtherNotifications(
    topic: string
  ): Promise<{ data: { message: string } }> {
    return this.requestBuilder
      .request('put', '/api/user/email-unsubscribe')
      .data({ topic, enabled: true })
      .send()
  }

  public disableOtherNotifications(
    topic: string
  ): Promise<{ data: { message: string } }> {
    return this.requestBuilder
      .request('delete', '/api/user/email-unsubscribe')
      .data({ topic, enabled: false })
      .send()
  }

  public fetchUnsubscribeInfo(
    token: string
  ): Promise<NotificationUnsubscribeInfo> {
    return this.requestBuilder
      .request('get', `/api/unsubscribe/${token}`)
      .send()
  }

  public unsubscribeFromTopic(topic: string) {
    return this.requestBuilder
      .request('delete', `/api/user/email-unsubscribe`)
      .data({ topic })
      .send()
  }

  public fetchNotification(
    pg: number,
    group: string = 'all'
  ): Promise<{
    notifications: { pagination: Pagination; rows: Array<NotificationEvent> }
    groups: Array<NotificationGroup>
    unreadNotificationsCount: number
    unseenNotificationsCount: number
    pinnedAnnouncement: NotificationEvent | null
  }> {
    return this.requestBuilder
      .request('get', '/api/user/notifications/')
      .params({
        pg,
        group,
        'see-notifications': 1
      })
      .send()
  }

  readNotifications(data: {
    notificationId?: number
    all?: boolean
    classifiedId?: number
  }): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', '/api/user/notifications/read/')
      .data(toSnakeCase(data))
      .validate(body => body)
      .send()
  }

  deleteNotification(notification: number) {
    return this.requestBuilder
      .request('delete', `/api/user/notifications/${notification}/delete`)
      .validate(body => body)
      .send()
  }

  enableAllGroupNotifications(group: string) {
    return this.requestBuilder
      .request('put', '/api/user/notifications/settings/groups')
      .data({ group })
      .send()
  }

  disableAllGroupNotifications(group: string) {
    return this.requestBuilder
      .request('delete', '/api/user/notifications/settings/groups')
      .data({ group })
      .send()
  }

  enableAllNotifications(): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', '/api/user/notifications/settings/enable-all')
      .send()
  }

  handleError(error: AxiosError) {
    const response = error.response
    if (response) {
      switch (response.status) {
        case 400: {
          this.snackbar.error(response.data.error)
          break
        }
        default: {
          this.snackbar.error(response.data.error)
          this.logger.captureError(error)
          break
        }
      }
    } else {
      this.snackbar.error(
        this.i18n.t('an error occurred please try again later')
      )
      this.logger.captureError(error)
    }
  }
}
